import { ApiClientCredential } from '@era/settings-sdk';
import { createFeatureSelector, createSelector, MemoizedSelector, Selector } from '@ngrx/store';
import { Dictionary } from 'lodash';
import { ApiClientSettingStoreSelectors } from '../api-client-setting';
import { Category } from '../router/category';
import * as fromCategoryApiClientSetting from './category-api-client-setting.reducer';
import { ApiClientCredentialsConfiguration, CategoryApiClientSettingState } from './category-api-client-setting.state';

// ============================================================================
// STATE SELECTORS
// ============================================================================

export const selectCategoryApiClientSettingState =
  createFeatureSelector<CategoryApiClientSettingState>('categoryApiClientSetting');

export const selectAllCategoryApiClientSettings = createSelector(
  selectCategoryApiClientSettingState,
  fromCategoryApiClientSetting.selectAllCategoryApiClientSettings
);

export const selectCategoryApiClientSettingEntities: Selector<
  object,
  Dictionary<ApiClientCredentialsConfiguration>
> = createSelector(
  selectCategoryApiClientSettingState,
  fromCategoryApiClientSetting.selectCategoryApiClientSettingEntities
);

// ============================================================================
// FACTORY FUNCTIONS
// ============================================================================

export const createSelectApiClientSettingLinkedToCategory = (category: Category) => {
  return createSelector(
    selectCategoryApiClientSettingEntities,
    (configurations: Dictionary<ApiClientCredentialsConfiguration>) => {
      if (!category || !configurations) {
        return undefined;
      }
      const key = `${category.categoryType}-${category.categoryId}`;
      return configurations[key];
    }
  );
};

// ============================================================================
// GENERIC SELECTORS
// ============================================================================

/**
 * Generic selector for checking if API client setting is loaded for any category type.
 *
 * Usage in containers:
 * this.settingLoaded$ = this.store.select(selectApiClientSettingLinkedToCategoryLoaded(category));
 */
export const selectApiClientSettingLinkedToCategoryLoaded = (category: Category): MemoizedSelector<object, boolean> => {
  return createSelector(createSelectApiClientSettingLinkedToCategory(category), (configuration) => !!configuration);
};

/**
 * Generic selector for API client credential linked to any category type.
 *
 * Usage in containers:
 * this.credential$ = this.store.select(selectApiClientCredentialLinkedToCategory(category));
 */
export const selectApiClientCredentialLinkedToCategory = (
  category: Category
): MemoizedSelector<object, ApiClientCredential | undefined> => {
  return createSelector(
    createSelectApiClientSettingLinkedToCategory(category),
    ApiClientSettingStoreSelectors.selectApiClientSettingEntities,
    (configuration: ApiClientCredentialsConfiguration, apiClientCredentials) =>
      apiClientCredentials && configuration ? apiClientCredentials[configuration.apiClientCredentialsId] : undefined
  );
};

/**
 * Generic selector for checking if API client credential is linked to any category type.
 *
 * Usage in containers:
 * this.hasCredential$ = this.store.select(selectHasApiClientCredentialLinkedToCategory('Brand'));
 */
export const selectHasApiClientCredentialLinkedToCategory = (category: Category): MemoizedSelector<object, boolean> => {
  return createSelector(
    selectApiClientCredentialLinkedToCategory(category),
    (apiClientCredential) => !!apiClientCredential
  );
};

/**
 * Generic selector for API client credential applied to any category type (with inheritance).
 * Location inherits from Brand, Room inherits from Location (or Brand if Location has none).
 *
 * Usage in containers:
 * this.appliedCredential$ = this.store.select(selectApiClientCredentialAppliedToCategory('Location'));
 */
export const selectApiClientCredentialAppliedToCategory = (
  category: Category
): MemoizedSelector<object, ApiClientCredential | undefined> => {
  if (category.parent) {
    return createSelector(
      selectApiClientCredentialAppliedToCategory(category.parent),
      selectApiClientCredentialLinkedToCategory(category),
      (parent, current) => current ?? parent
    );
  } else {
    return selectApiClientCredentialLinkedToCategory(category);
  }
};
